/* eslint-disable no-restricted-syntax */

import {
  conditionFormationType,
  IFeedback,
  IFeedbackList,
  IselecedOverviewDetails,
  TotalResultType} from "../../types/feedback";

/** Keep reportees under their managers from the first overview render. */
export const getRootFeedbackList = (feedbackList: IFeedbackList): IFeedbackList => {
  const reporteeIds = new Set<string>();
  feedbackList.forEach((feedback) => {
    feedback.responsible_person?.reportees?.forEach((reportee: unknown) => {
      let id: unknown = reportee;
      if (typeof reportee === "object" && reportee !== null) {
        // The overview API sends tracker links, rather than person objects.
        if ("reportee_id" in reportee) {
          id = reportee.reportee_id;
        } else if ("id" in reportee) {
          id = reportee.id;
        } else {
          id = undefined;
        }
      }
      if (typeof id === "string" || typeof id === "number") {
        reporteeIds.add(String(id));
      }
    });
  });
  return feedbackList.filter(
    (feedback) => !reporteeIds.has(String(feedback.responsible_person?.id))
  );
};

/**
 * Retrieves feedback information for a given target key by searching through a list of feedback items.
 * If the target key matches a responsible person's ID or is found in nested child feedback, it returns the corresponding feedback.
 *
 * @param feedbackList - The list of feedback items to search through.
 * @param targetKey - The ID of the target responsible person to match.
 * @returns The feedback object that matches the target key, or null if no match is found.
 */
export const getFeedbackClickedOverviewInfo: (
  feedbackList: IFeedbackList,
  targetKey: string
) => null | IFeedback = (
  feedbackListData: IFeedbackList,
  targetKey: string
) => {
  for (const feedback of feedbackListData) {
    if (feedback.responsible_person.id === targetKey) {
      return feedback;
    }
    if (feedback?.children && feedback?.children?.length > 0) {
      const clickedMenuItem: IFeedback | null = getFeedbackClickedOverviewInfo(
        feedback.children,
        targetKey
      );
      if (clickedMenuItem) {
        return clickedMenuItem;
      }
    }
  }
  return null;
};

/**
 * Searches for feedback associated with a specific person ID within a list of feedback items.
 * The function recursively traverses through nested feedback children to find a match.
 *
 * @param feedbackList - The list of feedback items to search through.
 * @param personId - The ID of the person to match against the responsible_person ID.
 * @returns The feedback object associated with the specified person ID, or null if no match is found.
 */

export const getFeedbackByPersonId: (
  feedbackList: IFeedbackList,
  personId: string
) => null | IFeedback = (feedbackListData: IFeedbackList, personId: string) => {
  for (const feedback of feedbackListData) {
    if (feedback?.responsible_person?.id === personId) {
      return feedback;
    }
    if (feedback?.children && feedback?.children?.length > 0) {
      const clickedMenuItem: IFeedback | null = getFeedbackByPersonId(
        feedback.children,
        personId
      );
      if (clickedMenuItem) {
        return clickedMenuItem;
      }
    }
  }
  return null;
};

/**
 * Calculates the percentage of the total rating out of the possible rating.
 * If either the total rating or possible rating is zero or negative, it returns 0.
 *
 * @param totalRatting - The total rating value.
 * @param possibleRating - The maximum possible rating value.
 * @returns The percentage value as a number, or undefined if inputs are invalid.
 */

export const getPercentage: (
  totalRatting: number,
  possibleRating: number
) => number | undefined = (totalRatting: number, possibleRating: number) => {
  if (possibleRating <= 0 || !possibleRating) {
    return undefined;
  }
  if (totalRatting > 0 && possibleRating > 0) {
    return (100 * totalRatting) / possibleRating;
  }
  return 0;
};

/**
 * Determines a CSS class name based on a given percentage and predefined threshold conditions.
 * The function categorizes the percentage into specific tags (e.g., green, yellow, orange, red)
 * using default or custom threshold values.
 *
 * @param percentage - The percentage value to evaluate (can be undefined).
 * @param conditionFormation - An array of condition objects specifying custom thresholds.
 * @returns A string representing the CSS class name corresponding to the percentage category.
 */

export const getPercentageClass: (
  percentage: number | undefined,
  conditionFormation: conditionFormationType[]
) => string = (
  percentage: number | undefined,
  conditionFormation: conditionFormationType[]
) => {
  let percentageA: number = 88;
  let percentageB: number = 75;
  let percentageC: number = 60;
  let percentageD: number = 0;
  if (conditionFormation && conditionFormation.length > 0) {
    percentageA = conditionFormation[0].threshold;
    percentageB = conditionFormation[1].threshold;
    percentageC = conditionFormation[2].threshold;
    percentageD = conditionFormation[3].threshold;
  }

  let className = "red-tag";
  if (percentage === undefined) {
    className = "red-tag empty-value-feedback";
  }
  if (percentage && percentage > 0) {
    if (percentage >= percentageA) {
      className = "green-tag";
    } else if (percentage >= percentageB) {
      className = "yellow-tag";
    } else if (percentage >= percentageC) {
      className = "orange-tag";
    }
  }
  return className;
};

/**
 * Generates a detailed breakdown of feedback data for a specific ID across various categories
 * (e.g., Player, Objectives, KPIs, Initiatives, etc.) and quarters (Q1 to Q4).
 * Calculates and includes the percentage of ratings for each quarter based on total and possible ratings.
 *
 * @param feedback - The selected overview feedback details containing rating information for multiple categories and quarters.
 * @param id - The unique identifier associated with the feedback being processed.
 * @returns An array of TotalResultType objects containing the detailed feedback data for each category and quarter.
 */

export const getFeedbackTotalResultData: (
  feedback: IselecedOverviewDetails,
  id: string
) => TotalResultType[] = (feedback: IselecedOverviewDetails, id) => {
  const totalResult: TotalResultType[] = [];

  totalResult.push({
    key: `player-${id}`,
    name: "player",
    firstQuater: `${
      feedback?.player.q1.player
        ? feedback?.player.q1?.player.toUpperCase()
        : ""
    }`,
    secondQuater: `${
      feedback?.player.q2.player
        ? feedback?.player.q2?.player.toUpperCase()
        : ""
    }`,
    thirdQuater: `${
      feedback?.player.q3.player
        ? feedback?.player.q3?.player.toUpperCase()
        : ""
    }`,
    fourthQuater: `${
      feedback?.player.q4.player
        ? feedback?.player.q4?.player.toUpperCase()
        : ""
    }`,
    firstQuaterPercentage: getPercentage(
      feedback?.player.q1.total_rating,
      feedback?.player.q1.possible_rating
    ),
    secondQuaterPercentage: getPercentage(
      feedback?.player.q2.total_rating,
      feedback?.player.q2.possible_rating
    ),
    thirdQuaterPercentage: getPercentage(
      feedback?.player.q3.total_rating,
      feedback?.player.q3.possible_rating
    ),
    fourthQuaterPercentage: getPercentage(
      feedback?.player.q4.total_rating,
      feedback?.player.q4.possible_rating
    ),
    quarterlyVisibility: feedback.quarter_visibility});
  totalResult.push({
    key: `total-${id}`,
    name: "total",
    firstQuater: `${
      feedback?.player.q1.total_rating >= 0
        ? `${feedback?.player.q1.total_rating} / ${feedback?.player.q1.possible_rating}`
        : ""
    }`,
    secondQuater: `${
      feedback?.player.q2.total_rating >= 0
        ? `${feedback?.player.q2.total_rating} / ${feedback?.player.q2.possible_rating}`
        : ""
    }`,
    thirdQuater: `${
      feedback?.player.q3.total_rating >= 0
        ? `${feedback?.player.q3.total_rating} / ${feedback?.player.q3.possible_rating}`
        : ""
    }`,
    fourthQuater: `${
      feedback?.player.q4.total_rating >= 0
        ? `${feedback?.player.q4.total_rating} / ${feedback?.player.q4.possible_rating}`
        : ""
    }`,
    firstQuaterPercentage: getPercentage(
      feedback?.player.q1.total_rating,
      feedback?.player.q1.possible_rating
    ),
    secondQuaterPercentage: getPercentage(
      feedback?.player.q2.total_rating,
      feedback?.player.q2.possible_rating
    ),
    thirdQuaterPercentage: getPercentage(
      feedback?.player.q3.total_rating,
      feedback?.player.q3.possible_rating
    ),
    fourthQuaterPercentage: getPercentage(
      feedback?.player.q4.total_rating,
      feedback?.player.q4.possible_rating
    ),
    quarterlyVisibility: feedback.quarter_visibility});
  totalResult.push({
    key: `objectives-${id}`,
    name: "objectives",
    firstQuater: `${
      feedback?.objectives.q1.total_rating >= 0
        ? `${feedback?.objectives.q1.total_rating} / ${feedback?.objectives.q1.possible_rating}`
        : ""
    }`,
    secondQuater: `${
      feedback?.objectives.q2.total_rating >= 0
        ? `${feedback?.objectives.q2.total_rating} / ${feedback?.objectives.q2.possible_rating}`
        : ""
    }`,
    thirdQuater: `${
      feedback?.objectives.q3.total_rating >= 0
        ? `${feedback?.objectives.q3.total_rating} / ${feedback?.objectives.q3.possible_rating}`
        : ""
    }`,
    fourthQuater: `${
      feedback?.objectives.q4.total_rating >= 0
        ? `${feedback?.objectives.q4.total_rating} / ${feedback?.objectives.q4.possible_rating}`
        : ""
    }`,
    firstQuaterPercentage: getPercentage(
      feedback?.objectives.q1.total_rating,
      feedback?.objectives.q1.possible_rating
    ),
    secondQuaterPercentage: getPercentage(
      feedback?.objectives.q2.total_rating,
      feedback?.objectives.q2.possible_rating
    ),
    thirdQuaterPercentage: getPercentage(
      feedback?.objectives.q3.total_rating,
      feedback?.objectives.q3.possible_rating
    ),
    fourthQuaterPercentage: getPercentage(
      feedback?.objectives.q4.total_rating,
      feedback?.objectives.q4.possible_rating
    ),
    quarterlyVisibility: feedback.quarter_visibility});
  totalResult.push({
    key: `kpis-${id}`,
    name: "kpis",
    firstQuater: `${
      feedback?.kpi.q1.total_rating >= 0
        ? `${feedback?.kpi.q1.total_rating} / ${feedback?.kpi.q1.possible_rating}`
        : ""
    }`,
    secondQuater: `${
      feedback?.kpi.q2.total_rating >= 0
        ? `${feedback?.kpi.q2.total_rating} / ${feedback?.kpi.q2.possible_rating}`
        : ""
    }`,
    thirdQuater: `${
      feedback?.kpi.q3.total_rating >= 0
        ? `${feedback?.kpi.q3.total_rating} / ${feedback?.kpi.q3.possible_rating}`
        : ""
    }`,
    fourthQuater: `${
      feedback?.kpi.q4.total_rating >= 0
        ? `${feedback?.kpi.q4.total_rating} / ${feedback?.kpi.q4.possible_rating}`
        : ""
    }`,
    firstQuaterPercentage: getPercentage(
      feedback?.kpi.q1.total_rating,
      feedback?.kpi.q1.possible_rating
    ),
    secondQuaterPercentage: getPercentage(
      feedback?.kpi.q2.total_rating,
      feedback?.kpi.q2.possible_rating
    ),
    thirdQuaterPercentage: getPercentage(
      feedback?.kpi.q3.total_rating,
      feedback?.kpi.q3.possible_rating
    ),
    fourthQuaterPercentage: getPercentage(
      feedback?.kpi.q4.total_rating,
      feedback?.kpi.q4.possible_rating
    ),
    quarterlyVisibility: feedback.quarter_visibility});
  totalResult.push({
    key: `initiatives-${id}`,
    name: "initiatives",
    firstQuater: `${
      feedback?.initiatives.q1.total_rating >= 0
        ? `${feedback?.initiatives.q1.total_rating} / ${feedback?.initiatives.q1.possible_rating}`
        : ""
    }`,
    secondQuater: `${
      feedback?.initiatives.q2.total_rating >= 0
        ? `${feedback?.initiatives.q2.total_rating} / ${feedback?.initiatives.q2.possible_rating}`
        : ""
    }`,
    thirdQuater: `${
      feedback?.initiatives.q3.total_rating >= 0
        ? `${feedback?.initiatives.q3.total_rating} / ${feedback?.initiatives.q3.possible_rating}`
        : ""
    }`,
    fourthQuater: `${
      feedback?.initiatives.q4.total_rating >= 0
        ? `${feedback?.initiatives.q4.total_rating} / ${feedback?.initiatives.q4.possible_rating}`
        : ""
    }`,
    firstQuaterPercentage: getPercentage(
      feedback?.initiatives.q1.total_rating,
      feedback?.initiatives.q1.possible_rating
    ),
    secondQuaterPercentage: getPercentage(
      feedback?.initiatives.q2.total_rating,
      feedback?.initiatives.q2.possible_rating
    ),
    thirdQuaterPercentage: getPercentage(
      feedback?.initiatives.q3.total_rating,
      feedback?.initiatives.q3.possible_rating
    ),
    fourthQuaterPercentage: getPercentage(
      feedback?.initiatives.q4.total_rating,
      feedback?.initiatives.q4.possible_rating
    ),
    quarterlyVisibility: feedback.quarter_visibility});
  totalResult.push({
    key: `actions-${id}`,
    name: "actionsActivitiesProccess",
    firstQuater: `${
      feedback?.actions_activities_and_processes.q1.total_rating >= 0
        ? `${feedback?.actions_activities_and_processes.q1.total_rating} / ${feedback?.actions_activities_and_processes.q1.possible_rating}`
        : ""
    }`,
    secondQuater: `${
      feedback?.actions_activities_and_processes.q2.total_rating >= 0
        ? `${feedback?.actions_activities_and_processes.q2.total_rating} / ${feedback?.actions_activities_and_processes.q2.possible_rating}`
        : ""
    }`,
    thirdQuater: `${
      feedback?.actions_activities_and_processes.q3.total_rating >= 0
        ? `${feedback?.actions_activities_and_processes.q3.total_rating} / ${feedback?.actions_activities_and_processes.q3.possible_rating}`
        : ""
    }`,
    fourthQuater: `${
      feedback?.actions_activities_and_processes.q4.total_rating >= 0
        ? `${feedback?.actions_activities_and_processes.q4.total_rating} / ${feedback?.actions_activities_and_processes.q4.possible_rating}`
        : ""
    }`,
    firstQuaterPercentage: getPercentage(
      feedback?.actions_activities_and_processes.q1.total_rating,
      feedback?.actions_activities_and_processes.q1.possible_rating
    ),
    secondQuaterPercentage: getPercentage(
      feedback?.actions_activities_and_processes.q2.total_rating,
      feedback?.actions_activities_and_processes.q2.possible_rating
    ),
    thirdQuaterPercentage: getPercentage(
      feedback?.actions_activities_and_processes.q3.total_rating,
      feedback?.actions_activities_and_processes.q3.possible_rating
    ),
    fourthQuaterPercentage: getPercentage(
      feedback?.actions_activities_and_processes.q4.total_rating,
      feedback?.actions_activities_and_processes.q4.possible_rating
    ),
    quarterlyVisibility: feedback.quarter_visibility});
  totalResult.push({
    key: `culture-${id}`,
    name: "culture",
    firstQuater: `${
      feedback?.culture.q1.total_rating >= 0
        ? `${feedback?.culture.q1.total_rating} / ${feedback?.culture.q1.possible_rating}`
        : ""
    }`,
    secondQuater: `${
      feedback?.culture.q2.total_rating >= 0
        ? `${feedback?.culture.q2.total_rating} / ${feedback?.culture.q2.possible_rating}`
        : ""
    }`,
    thirdQuater: `${
      feedback?.culture.q3.total_rating >= 0
        ? `${feedback?.culture.q3.total_rating} / ${feedback?.culture.q3.possible_rating}`
        : ""
    }`,
    fourthQuater: `${
      feedback?.culture.q4.total_rating >= 0
        ? `${feedback?.culture.q4.total_rating} / ${feedback?.culture.q4.possible_rating}`
        : ""
    }`,
    firstQuaterPercentage: getPercentage(
      feedback?.culture.q1.total_rating,
      feedback?.culture.q1.possible_rating
    ),
    secondQuaterPercentage: getPercentage(
      feedback?.culture.q2.total_rating,
      feedback?.culture.q2.possible_rating
    ),
    thirdQuaterPercentage: getPercentage(
      feedback?.culture.q3.total_rating,
      feedback?.culture.q3.possible_rating
    ),
    fourthQuaterPercentage: getPercentage(
      feedback?.culture.q4.total_rating,
      feedback?.culture.q4.possible_rating
    ),
    quarterlyVisibility: feedback.quarter_visibility});

  return totalResult;
};

/**
 * Updates the children of a specific feedback item within a nested feedback list based on a target key.
 * Recursively traverses the feedback list to find the matching item by its responsible person's ID and updates its children.
 *
 * @param feedbackListData - The list of feedback items to search and update.
 * @param targetKey - The ID of the responsible person whose feedback item needs updating.
 * @param childData - The new children data to be assigned to the matching feedback item.
 * @returns The updated feedback list with the specified item's children updated.
 */
export const findOverviewFeedbackAndUpdateChildren: (
  feedbackListData: IFeedbackList,
  targetKey: string,
  childData: IFeedbackList
) => IFeedbackList = (feedbackListData, targetKey, childData) => {
  // Collect IDs of the new children (excluding the parent itself if returned by API)
  const newChildIds = new Set(
    childData
      ?.filter((overview) => String(overview?.responsible_person?.id) !== String(targetKey))
      ?.map((overview) => String(overview?.responsible_person?.id)) || []
  );

  const processNodes = (nodes: IFeedbackList): IFeedbackList => {
    return nodes
      ?.filter(
        // Remove nodes from their old positions so they don't appear as duplicates
        (feedback) => !newChildIds.has(String(feedback?.responsible_person?.id))
      )
      ?.map((feedback) => {
        if (String(feedback?.responsible_person?.id) === String(targetKey)) {
          const children = childData
            ?.filter(
              (overview) => String(overview?.responsible_person?.id) !== String(targetKey)
            )
            ?.map((overview) => {
              return {
                ...overview,
                key: overview?.feedback_overview_id,
                ...(overview?.responsible_person?.reportees?.length === 0
                  ? {}
                  : { children: [] }),
              };
            });
          return { ...feedback, children };
        }
        if (feedback.children && feedback.children.length > 0) {
          const updatedChildren = processNodes(feedback.children);
          return {
            ...feedback,
            ...(updatedChildren?.length === 0 ? {} : { children: updatedChildren }),
          };
        }
        return feedback;
      });
  };

  return processNodes(feedbackListData);
};
